/**
 * Índice de Serviços
 * Exporta todos os serviços do SDK de forma organizada
 */

// Serviços de coleta de dados
export { CanvasService } from './CanvasService.js';
export { WebGLService } from './WebGLService.js';
export { AudioService } from './AudioService.js';
export { BehavioralService } from './BehavioralService.js';
export { DeviceService } from './DeviceService.js';
export { PrivacyService } from './PrivacyService.js';
export { NetworkService } from './NetworkService.js';
export { default as GeolocationService } from './GeolocationService.js';

// Serviços de orquestração e comunicação
export { FingerprintCollector } from './FingerprintCollector.js';
export { HttpService } from './HttpService.js';

/**
 * Factory para criar instâncias de serviços
 */
export class ServiceFactory {
    static createCanvasService(config, logger) {
        return new CanvasService(config, logger);
    }
    
    static createWebGLService(config, logger) {
        return new WebGLService(config, logger);
    }
    
    static createAudioService(config, logger) {
        return new AudioService(config, logger);
    }
    
    static createBehavioralService(config, logger) {
        return new BehavioralService(config, logger);
    }
    
    static createDeviceService(config, logger) {
        return new DeviceService(config, logger);
    }
    
    static createPrivacyService(config, logger) {
        return new PrivacyService(config, logger);
    }
    
    static createNetworkService(config, logger) {
        return new NetworkService(config, logger);
    }
    
    static createGeolocationService(config, logger) {
        return new GeolocationService(config, logger);
    }
    
    static createFingerprintCollector(config, logger, errorHandler) {
        return new FingerprintCollector(config, logger, errorHandler);
    }
    
    static createHttpService(config, logger, errorHandler) {
        return new HttpService(config, logger, errorHandler);
    }
    
    /**
     * Cria todos os serviços de uma vez
     */
    static createAllServices(config, logger, errorHandler) {
        return {
            canvas: ServiceFactory.createCanvasService(config, logger),
            webgl: ServiceFactory.createWebGLService(config, logger),
            audio: ServiceFactory.createAudioService(config, logger),
            behavioral: ServiceFactory.createBehavioralService(config, logger),
            device: ServiceFactory.createDeviceService(config, logger),
            privacy: ServiceFactory.createPrivacyService(config, logger),
            network: ServiceFactory.createNetworkService(config, logger),
            geolocation: ServiceFactory.createGeolocationService(config, logger),
            collector: ServiceFactory.createFingerprintCollector(config, logger, errorHandler),
            http: ServiceFactory.createHttpService(config, logger, errorHandler)
        };
    }
    
    /**
     * Verifica suporte de todos os serviços
     */
    static checkServicesSupport() {
        return {
            canvas: CanvasService.isSupported(),
            webgl: WebGLService.isSupported(),
            audio: AudioService.isSupported(),
            behavioral: BehavioralService.isSupported(),
            device: DeviceService.isSupported(),
            privacy: PrivacyService.isSupported(),
            network: NetworkService.isSupported(),
            collector: FingerprintCollector.isSupported(),
            http: HttpService.isSupported()
        };
    }
    
    /**
     * Obtém capacidades de todos os serviços
     */
    static getServicesCapabilities() {
        return {
            canvas: CanvasService.getCapabilities(),
            webgl: WebGLService.getCapabilities(),
            audio: AudioService.getCapabilities(),
            behavioral: BehavioralService.getCapabilities(),
            device: DeviceService.getCapabilities(),
            privacy: PrivacyService.getCapabilities(),
            network: NetworkService.getCapabilities(),
            collector: FingerprintCollector.getCapabilities ? FingerprintCollector.getCapabilities() : { supported: FingerprintCollector.isSupported() },
            http: HttpService.getCapabilities()
        };
    }
}

/**
 * Utilitários para serviços
 */
export class ServiceUtils {
    /**
     * Valida se um serviço está funcionando corretamente
     */
    static async validateService(service, serviceName) {
        try {
            // Verifica se o serviço tem os métodos básicos
            const requiredMethods = ['getStats'];
            for (const method of requiredMethods) {
                if (typeof service[method] !== 'function') {
                    throw new Error(`Serviço ${serviceName} não possui método ${method}`);
                }
            }
            
            // Tenta obter estatísticas
            const stats = service.getStats();
            if (!stats || typeof stats !== 'object') {
                throw new Error(`Serviço ${serviceName} retornou estatísticas inválidas`);
            }
            
            return {
                valid: true,
                serviceName,
                stats
            };
            
        } catch (error) {
            return {
                valid: false,
                serviceName,
                error: error.message
            };
        }
    }
    
    /**
     * Valida todos os serviços
     */
    static async validateAllServices(services) {
        const results = {};
        
        for (const [name, service] of Object.entries(services)) {
            results[name] = await ServiceUtils.validateService(service, name);
        }
        
        return results;
    }
    
    /**
     * Obtém estatísticas de todos os serviços
     */
    static getAllServicesStats(services) {
        const stats = {};
        
        for (const [name, service] of Object.entries(services)) {
            try {
                if (service && typeof service.getStats === 'function') {
                    stats[name] = service.getStats();
                }
            } catch (error) {
                stats[name] = {
                    error: error.message
                };
            }
        }
        
        return stats;
    }
    
    /**
     * Limpa caches de todos os serviços
     */
    static clearAllServicesCaches(services) {
        const results = {};
        
        for (const [name, service] of Object.entries(services)) {
            try {
                if (service && typeof service.clearCache === 'function') {
                    service.clearCache();
                    results[name] = { success: true };
                } else {
                    results[name] = { success: false, reason: 'Método clearCache não disponível' };
                }
            } catch (error) {
                results[name] = { success: false, error: error.message };
            }
        }
        
        return results;
    }
    
    /**
     * Atualiza configuração de todos os serviços
     */
    static updateAllServicesConfig(services, newConfig) {
        const results = {};
        
        for (const [name, service] of Object.entries(services)) {
            try {
                if (service && typeof service.updateConfig === 'function') {
                    service.updateConfig(newConfig);
                    results[name] = { success: true };
                } else {
                    results[name] = { success: false, reason: 'Método updateConfig não disponível' };
                }
            } catch (error) {
                results[name] = { success: false, error: error.message };
            }
        }
        
        return results;
    }
    
    /**
     * Destrói todos os serviços
     */
    static destroyAllServices(services) {
        const results = {};
        
        for (const [name, service] of Object.entries(services)) {
            try {
                if (service && typeof service.destroy === 'function') {
                    service.destroy();
                    results[name] = { success: true };
                } else {
                    results[name] = { success: false, reason: 'Método destroy não disponível' };
                }
            } catch (error) {
                results[name] = { success: false, error: error.message };
            }
        }
        
        return results;
    }
}

/**
 * Constantes de serviços
 */
export const SERVICE_NAMES = {
    CANVAS: 'canvas',
    WEBGL: 'webgl',
    AUDIO: 'audio',
    BEHAVIORAL: 'behavioral',
    DEVICE: 'device',
    PRIVACY: 'privacy',
    NETWORK: 'network',
    GEOLOCATION: 'geolocation',
    COLLECTOR: 'collector',
    HTTP: 'http'
};

export const SERVICE_PRIORITIES = {
    HIGH: ['device', 'canvas', 'webgl'],
    MEDIUM: ['audio', 'privacy', 'network', 'geolocation'],
    LOW: ['behavioral']
};

export const SERVICE_CATEGORIES = {
    FINGERPRINTING: ['canvas', 'webgl', 'audio', 'device'],
    BEHAVIORAL: ['behavioral'],
    SECURITY: ['privacy', 'network'],
    GEOLOCATION: ['geolocation'],
    INFRASTRUCTURE: ['collector', 'http']
};

/**
 * Configurações padrão para serviços
 */
export const DEFAULT_SERVICE_CONFIG = {
    canvas: {
        enabled: true,
        cacheEnabled: true,
        cacheTimeout: 300000, // 5 minutos
        techniques: ['text', 'geometry', 'gradients']
    },
    webgl: {
        enabled: true,
        cacheEnabled: true,
        cacheTimeout: 300000,
        detectVirtualization: true
    },
    audio: {
        enabled: true,
        cacheEnabled: true,
        cacheTimeout: 300000,
        oscillatorFrequency: 1000
    },
    behavioral: {
        enabled: true,
        maxEvents: 1000,
        trackMouse: true,
        trackKeyboard: true,
        trackTouch: true,
        trackFocus: true
    },
    device: {
        enabled: true,
        cacheEnabled: true,
        cacheTimeout: 600000, // 10 minutos
        collectHardwareInfo: true,
        collectNetworkInfo: true
    },
    privacy: {
        enabled: true,
        cacheEnabled: true,
        cacheTimeout: 300000,
        detectIncognito: true,
        detectVPN: true,
        detectProxy: true
    },
    network: {
        enabled: true,
        cacheEnabled: true,
        cacheTimeout: 300000,
        collectIPs: true,
        checkConsistency: true
    },
    geolocation: {
        enabled: true,
        enableIPGeolocation: true,
        enableBrowserGeolocation: true,
        timeout: 5000
    },
    http: {
        timeout: 10000,
        maxRetries: 3,
        retryDelay: 1000,
        maxQueueSize: 100
    }
};

/**
 * Versões dos serviços
 */
export const SERVICE_VERSIONS = {
    canvas: '1.0.0',
    webgl: '1.0.0',
    audio: '1.0.0',
    behavioral: '1.0.0',
    device: '1.0.0',
    privacy: '1.0.0',
    network: '1.0.0',
    geolocation: '1.0.0',
    collector: '1.0.0',
    http: '1.0.0'
};

/**
 * Metadados dos serviços
 */
export const SERVICE_METADATA = {
    canvas: {
        name: 'Canvas Service',
        description: 'Gera fingerprint usando Canvas 2D',
        category: 'fingerprinting',
        priority: 'high'
    },
    webgl: {
        name: 'WebGL Service',
        description: 'Gera fingerprint usando WebGL',
        category: 'fingerprinting',
        priority: 'high'
    },
    audio: {
        name: 'Audio Service',
        description: 'Gera fingerprint usando Web Audio API',
        category: 'fingerprinting',
        priority: 'medium'
    },
    behavioral: {
        name: 'Behavioral Service',
        description: 'Coleta dados comportamentais do usuário',
        category: 'behavioral',
        priority: 'low'
    },
    device: {
        name: 'Device Service',
        description: 'Coleta informações do dispositivo',
        category: 'fingerprinting',
        priority: 'high'
    },
    privacy: {
        name: 'Privacy Service',
        description: 'Detecta recursos de privacidade e ambientes suspeitos',
        category: 'security',
        priority: 'medium'
    },
    network: {
        name: 'Network Service',
        description: 'Analisa informações de rede',
        category: 'security',
        priority: 'medium'
    },
    geolocation: {
        name: 'Geolocation Service',
        description: 'Obtém dados de geolocalização por IP e navegador',
        category: 'geolocation',
        priority: 'medium'
    },
    collector: {
        name: 'Fingerprint Collector',
        description: 'Orquestra a coleta de todos os dados de fingerprint',
        category: 'infrastructure',
        priority: 'high'
    },
    http: {
        name: 'HTTP Service',
        description: 'Gerencia comunicações HTTP do SDK',
        category: 'infrastructure',
        priority: 'high'
    }
};